import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ToolPageShell } from "@/components/AdminTools";
import { useAuth } from "@/lib/auth-context";
import { Check, CreditCard, ShieldCheck, Loader2, Receipt, Sparkles } from "lucide-react";

const TIER_ACCENTS = {
  growth: { ring: "ring-blue-200", chip: "bg-blue-50 text-blue-700", btn: "bg-blue-600 hover:bg-blue-500" },
  scale: { ring: "ring-violet-200", chip: "bg-violet-50 text-violet-700", btn: "bg-violet-600 hover:bg-violet-500" },
  enterprise: { ring: "ring-amber-200", chip: "bg-amber-50 text-amber-700", btn: "bg-slate-900 hover:bg-slate-800" },
};

function formatUsd(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
}

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function PortalBilling() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyPlanId, setBusyPlanId] = useState(null);

  const activePlanId = user?.plan_id || null;

  const load = async () => {
    setLoading(true);
    try {
      const [p, t] = await Promise.all([
        api.get("/payments/plans"),
        api.get("/payments/transactions"),
      ]);
      setPlans(p.data?.plans || []);
      setTransactions(t.data?.transactions || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startCheckout = async (planId) => {
    setBusyPlanId(planId);
    try {
      const r = await api.post("/payments/checkout/plan", {
        package_id: planId,
        origin_url: window.location.origin,
      });
      const url = r.data?.url;
      if (!url) {
        toast.error("Stripe didn’t return a checkout URL.");
        return;
      }
      // Hand off to Stripe Checkout. Cancel returns to /portal/billing,
      // success returns to /portal/billing/return?session_id=...
      window.location.href = url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusyPlanId(null);
    }
  };

  return (
    <ToolPageShell
      eyebrow="Billing"
      title="Plans & Payments"
      intro="Choose the Nodera plan that fits your clinic. Secure checkout powered by Stripe — your card never touches our servers."
    >
      <div data-testid="portal-billing" className="space-y-12">
        {/* Active plan banner */}
        <div
          data-testid="active-plan-banner"
          className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-5 flex flex-wrap items-center justify-between gap-4"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600/10 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">
                Current plan
              </div>
              <div className="text-lg font-semibold text-slate-900" data-testid="current-plan-name">
                {user?.plan_name || "Nodera Starter (Free)"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <ShieldCheck className="w-4 h-4 text-emerald-500" />
            <span>Encrypted by Stripe · PCI-DSS Level 1</span>
          </div>
        </div>

        {/* Plan cards */}
        <div className="grid gap-5 lg:grid-cols-3" data-testid="plans-grid">
          {loading && (
            <div className="lg:col-span-3 text-sm text-slate-500 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading plans…
            </div>
          )}
          {!loading &&
            plans.map((plan) => {
              const accent = TIER_ACCENTS[plan.id] || TIER_ACCENTS.growth;
              const isActive = activePlanId === plan.id;
              const isBusy = busyPlanId === plan.id;
              return (
                <div
                  key={plan.id}
                  data-testid={`plan-card-${plan.id}`}
                  className={`rounded-2xl border border-slate-200 bg-white p-6 flex flex-col gap-5 transition-shadow hover:shadow-lg ring-1 ${accent.ring} ${
                    isActive ? "ring-2 ring-emerald-300" : ""
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider ${accent.chip}`}>
                        {plan.id}
                      </div>
                      <h3 className="mt-2 font-display text-2xl font-extrabold tracking-tight text-slate-900">
                        {plan.name}
                      </h3>
                      <p className="mt-1 text-sm text-slate-600">{plan.tagline}</p>
                    </div>
                    {isActive && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700">
                        <Check className="w-3 h-3" /> Active
                      </span>
                    )}
                  </div>

                  <div className="border-t border-slate-100 pt-4">
                    <div className="flex items-baseline gap-1">
                      <span className="font-display text-4xl font-extrabold tracking-tight text-slate-900">
                        {formatUsd(plan.amount)}
                      </span>
                      <span className="text-sm text-slate-500">/ month</span>
                    </div>
                  </div>

                  <ul className="space-y-2 text-sm text-slate-700 flex-1">
                    {plan.highlights.map((h, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    type="button"
                    data-testid={`plan-checkout-btn-${plan.id}`}
                    onClick={() => startCheckout(plan.id)}
                    disabled={isBusy || isActive}
                    className={`w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${accent.btn}`}
                  >
                    {isBusy ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Redirecting to Stripe…
                      </>
                    ) : isActive ? (
                      <>Already on this plan</>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4" /> Upgrade — {formatUsd(plan.amount)}
                      </>
                    )}
                  </button>
                </div>
              );
            })}
        </div>

        {/* Transactions table */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Receipt className="w-4 h-4 text-slate-500" />
            <h2 className="font-display text-xl font-bold text-slate-900">Payment history</h2>
          </div>
          <div data-testid="transactions-table" className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            {transactions.length === 0 ? (
              <div className="p-6 text-sm text-slate-500">
                No payments yet. Once you upgrade or activate a paid program, your receipts will appear here.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Date</th>
                    <th className="text-left px-4 py-3 font-semibold">Item</th>
                    <th className="text-left px-4 py-3 font-semibold">Amount</th>
                    <th className="text-left px-4 py-3 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((t) => (
                    <tr
                      key={t.session_id}
                      data-testid={`transaction-row-${t.session_id}`}
                      className="border-t border-slate-100"
                    >
                      <td className="px-4 py-3 text-slate-600">{formatDateTime(t.created_at)}</td>
                      <td className="px-4 py-3 text-slate-900 font-medium">
                        {t.package_name || t.program_name || "—"}
                        <div className="text-xs text-slate-500 font-normal">
                          {t.kind === "platform_plan" ? "Plan upgrade" : "Program activation"}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-900">{formatUsd(t.amount)}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                            t.payment_status === "paid"
                              ? "bg-emerald-50 text-emerald-700"
                              : t.payment_status === "pending"
                                ? "bg-amber-50 text-amber-700"
                                : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {t.payment_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </ToolPageShell>
  );
}
